import React from 'react';
import styled from 'styled-components';
import * as _ from 'lodash';
import { Link } from 'react-router-dom';
import { Group } from '@vx/group';
import { arc as d3Arc, pie as d3Pie } from 'd3-shape';

interface CircularBarData {
  color: string;
  label?: string;
  value: number;
  link?: string;
}

interface CircularBarSettings {
  diametr: number;
  section_padding: number;
  section_width: number;
}

interface CircularBarProps {
  className?: string;
  data: CircularBarData[];
  settings?: CircularBarSettings;
  isLabel?: boolean;
}

const LABEL_SIZE_STYLES = [
  {
    fontSize: '25px',
    lineHeight: '32px',
  },
  {
    fontSize: '18px',
    lineHeight: '18px',
  },
  {
    fontSize: '16px',
    lineHeight: '16px',
  },
];

class CircularBar extends React.Component<CircularBarProps, any> {
  static defaultProps: CircularBarProps = {
    data: [
      {
        color: 'grey',
        label: '',
        value: 0,
        link: '',
      },
    ],
    settings: {
      diametr: 200,
      section_padding: 0.03,
      section_width: 10,
    },
    isLabel: true,
  };

  shouldComponentUpdate(nextProps) {
    return !_.isEqual(this.props, nextProps);
  }

  getValue(item) {
    return item.value;
  }

  getColor(key) {
    return this.props.data[key].color;
  }

  getLabelStyle() {
    const { data } = this.props;
    return data.length <= 4
      ? LABEL_SIZE_STYLES[0]
      : data.length > 6 ? LABEL_SIZE_STYLES[2] : LABEL_SIZE_STYLES[1];
  }

  sortLabelsByValue({ value: a }, { value: b }) {
    if (a === b) {
      return 0;
    }
    if (a === undefined) {
      return 1;
    }
    if (b === undefined) {
      return -1;
    }
    return a < b ? 1 : -1;
  }

  getPaths() {
    const {
      data,
      settings: { diametr, section_padding, section_width },
    } = this.props;
    const radius = diametr / 2;
    const getPieData = d3Pie().value(this.getValue);
    const getPath = d3Arc()
      .outerRadius(radius)
      .padAngle(section_padding)
      .innerRadius(radius - section_width);

    return getPieData(data as any).map(path => getPath(path as any));
  }

  render() {
    const { className, data, settings: { diametr }, isLabel } = this.props;
    const radius = diametr / 2;

    return (
      <div className={className}>
        <svg className="circular-bar-pie" width={diametr} height={diametr}>
          <Group top={radius} left={radius}>
            {this.getPaths().map((path, key) => (
              <path
                key={key}
                fill={this.getColor(key)}
                stroke="white"
                d={path}
              />
            ))}
          </Group>
        </svg>
        {isLabel && (
          <div className="circular-bar-label">
            {data.sort(this.sortLabelsByValue).map((item, key) => (
              <Link
                to={item.link || ''}
                className="circular-bar-link"
                style={{ color: item.color }}
                key={key}
              >
                <div style={this.getLabelStyle()}>{item.value || 0}</div>
                <div>{item.label}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }
}

const StyledCircularBar = styled(CircularBar)`
  position: relative;
  margin: 2rem 4rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  .circular-bar-label {
    position: absolute;
    height: 100%;
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10%;
    .circular-bar-link {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      padding: 5px;
      text-decoration: none;
      :hover {
        color: black !important;
      }
    }
  }
`;

export default StyledCircularBar as any;
